import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Container } from "react-bootstrap";
import { FiUser } from "react-icons/fi";
import Footer from "../../components/footer/Footer";
import blogPosts from "./blogData";
import "./BlogDetail.css";

const BlogDetail = () => {
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const post = blogPosts.find((p) => p.slug === slug);

  const getRuntimeBase = () => {
    if (typeof window === "undefined") return "";

    const path = window.location.pathname || "";
    const hash = window.location.hash || "";
    const blogDetailIndex = path.indexOf("/blog/");

    if (blogDetailIndex > 0) {
      return path.slice(0, blogDetailIndex).replace(/\/$/, "");
    }

    if (path.endsWith("/blog")) {
      return path.slice(0, -5).replace(/\/$/, "");
    }

    // Support hash-router URLs like /my-app/#/blog/my-post.
    if (/^#\/?blog(\/|$)/i.test(hash) || /^#\/.*\/blog(\/|$)/i.test(hash)) {
      const baseFromPath = path.replace(/\/$/, "");
      return baseFromPath === "/" ? "" : baseFromPath;
    }

    return "";
  };

  const withPublicBase = (url) => {
    if (!url) return url;

    if (
      /^https?:\/\//i.test(url) ||
      url.startsWith("//") ||
      url.startsWith("data:") ||
      url.startsWith("blob:")
    ) {
      return url;
    }

    const rawBase = (process.env.PUBLIC_URL || "").trim();
    const configuredBase = rawBase === "." ? "" : rawBase.replace(/\/$/, "");
    const base = configuredBase || getRuntimeBase();

    if (url.startsWith("/")) {
      return `${base}${url}`;
    }

    // Convert relative blog asset paths to root-based paths before adding PUBLIC_URL.
    const normalizedPath = `/${url.replace(/^\.\/?/, "").replace(/^\/+/, "")}`;

    return `${base}${normalizedPath}`;
  };

  const htmlContentWithBase = useMemo(() => {
    if (!post?.htmlContent) return "";

    return post.htmlContent.replace(
      /(<img[^>]*\ssrc=["'])([^"']+)(["'])/gi,
      (_, prefix, src, suffix) => `${prefix}${withPublicBase(src)}${suffix}`,
    );
  }, [post]);

  if (!post) {
    return (
      <>
        <div className="blog_detail_not_found">
          <h2>Post Not Found</h2>
          <Link to="/blog" className="blog_detail_back">
            ← Back to Blog
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <section className="blog_detail_page">
        <Container className="blog_detail_container">
          {/* Back link */}
          <Link to="/blog" className="blog_detail_back">
            ← Back to Blog
          </Link>

          {/* Badges */}
          <div className="blog_detail_badges">
            {post.featured && (
              <span className="blog_detail_featured">Featured</span>
            )}
            {post.category && (
              <span className="blog_detail_category">{post.category}</span>
            )}
          </div>

          {/* Title & subtitle */}
          <h1 className="blog_detail_title">{post.title}</h1>
          {post.subtitle && (
            <p className="blog_detail_subtitle">{post.subtitle}</p>
          )}

          {/* Meta */}
          <div className="blog_detail_meta">
            {post.author && (
              <span className="blog_detail_author">
                <FiUser
                  className="blog_detail_author_icon"
                  aria-hidden="true"
                />
                {post.author}
              </span>
            )}
            {post.date && <span className="blog_detail_date">{post.date}</span>}
            {post.readTime && (
              <span className="blog_detail_readtime">{post.readTime}</span>
            )}
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="blog_detail_tags">
              {post.tags.map((tag, i) => (
                <span key={i}>{tag}</span>
              ))}
            </div>
          )}

          {/* Overview image */}
          {post.overviewImage && (
            <div className="blog_detail_image_wrap mt-4 mb-2">
              <img
                src={withPublicBase(post.overviewImage)}
                alt={post.title}
                className="blog_detail_image"
              />
            </div>
          )}

          {/* HTML content compiled from markdown at build time */}
          <div
            className="blog_md_content"
            dangerouslySetInnerHTML={{ __html: htmlContentWithBase }}
          />
        </Container>

        <Footer />
      </section>
    </>
  );
};

export default BlogDetail;
